import { useState } from 'react';
import type { WelfarePopupBlock } from '../../features/settings/types/welfare-popup.types';
import { RichTaskTextEditor } from '../../features/tasks/components/RichTaskTextEditor';
import { taskMarkupToEditorHtml } from '../../features/tasks/utils/taskRichTextMarkup';
import { resolveImagePreviewUrl, uploadImageToCos } from '../../lib/upload/cos';

const labels = { text: '富文本', image: '图片', video: '视频', audio: '音频' };
const buttonClass = 'rounded border border-gray-300 px-3 py-1.5 text-sm disabled:opacity-40';

export function WelfarePopupBlocksEditor({ value, onChange, disabled, onUploading }: {
  value: WelfarePopupBlock[];
  onChange: (blocks: WelfarePopupBlock[]) => void;
  disabled: boolean;
  onUploading: (busy: boolean) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const update = (index: number, patch: Partial<WelfarePopupBlock>) => onChange(value.map((block, i) => i === index ? { ...block, ...patch } : block));
  const move = (index: number, offset: number) => {
    const next = [...value];
    [next[index], next[index + offset]] = [next[index + offset], next[index]];
    onChange(next);
  };
  const upload = async (index: number, file?: File) => {
    if (!file) return;
    setBusy(true); onUploading(true); setError('');
    try {
      const result = await uploadImageToCos(file, 'home-popup');
      update(index, { url: result.readUrl });
    } catch (e) { setError(e instanceof Error ? e.message : '图片上传失败，请重试'); }
    finally { setBusy(false); onUploading(false); }
  };
  return <fieldset disabled={disabled || busy} className="mt-4 min-w-0 space-y-3">
    <legend className="text-sm font-medium text-gray-700">富媒体内容</legend>
    <p className="text-xs text-gray-500">按顺序展示，可添加多段富文本、图片、视频和音频。音视频填写 HTTPS 直链，需点击播放；最多 30 块。</p>
    {value.map((block, index) => <div key={index} className="min-w-0 space-y-2 rounded-md border border-gray-200 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="mr-auto text-sm font-medium">{index + 1}. {labels[block.type]}</span>
        <button type="button" className={buttonClass} disabled={index === 0} aria-label={`上移第${index + 1}块`} onClick={() => move(index, -1)}>上移</button>
        <button type="button" className={buttonClass} disabled={index === value.length - 1} aria-label={`下移第${index + 1}块`} onClick={() => move(index, 1)}>下移</button>
        <button type="button" className={buttonClass} aria-label={`删除第${index + 1}块`} onClick={() => onChange(value.filter((_, i) => i !== index))}>删除</button>
      </div>
      {block.type === 'text' ? <RichTaskTextEditor value={block.text || ''} onChange={text => update(index, { text })} minRows={3} disabled={disabled || busy} placeholder="支持加粗、红色和大字" /> : <>
        <input aria-label={`第${index + 1}块${labels[block.type]}地址`} value={block.url || ''} onChange={e => update(index, { url: e.target.value })} placeholder={block.type === 'image' ? '图片 URL 或上传图片' : 'https://… 媒体文件直链（非网页地址）'} className="h-9 w-full min-w-0 rounded-md border border-gray-300 px-3 text-sm" />
        {block.type === 'image' && <label className="block text-sm">上传图片 <input type="file" accept="image/*" aria-label={`上传第${index + 1}块图片`} onChange={e => { void upload(index, e.target.files?.[0]); e.target.value = ''; }} className="w-full text-sm" /></label>}
      </>}
    </div>)}
    <div className="flex flex-wrap gap-2">
      {(Object.keys(labels) as WelfarePopupBlock['type'][]).map(type => <button key={type} type="button" disabled={value.length >= 30} className={buttonClass} onClick={() => onChange([...value, type === 'text' ? { type, text: '' } : { type, url: '' }])}>添加{labels[type]}</button>)}
    </div>
    {busy && <p role="status" className="text-sm text-gray-500">图片上传中…</p>}
    {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
  </fieldset>;
}

export function WelfarePopupBlocksPreview({ blocks }: { blocks: WelfarePopupBlock[] }) {
  return <div className="space-y-3 break-words">{blocks.map((block, i) => {
    if (block.type === 'text') return <div key={i} className="text-sm leading-6 [&_.ql-size-large]:text-xl" dangerouslySetInnerHTML={{ __html: taskMarkupToEditorHtml(block.text || '') }} />;
    if (!block.url) return null;
    if (block.type === 'image') return <img key={i} src={resolveImagePreviewUrl(block.url)} alt={`内容图片 ${i + 1}`} className="max-h-72 w-full object-contain" />;
    if (!/^https:\/\//i.test(block.url)) return null;
    if (block.type === 'video') return <video key={`${i}-${block.url}`} src={block.url} controls playsInline preload="none" className="w-full" />;
    return <audio key={`${i}-${block.url}`} src={block.url} controls preload="none" className="w-full" />;
  })}</div>;
}
